import { cn } from "@/lib/utils"
import type { ReactNode } from "react"
import { useElementWidth } from "./useElementWidth"

export interface FieldDef {
  label: string
  value: ReactNode
  /** Span both columns — use for wide values like long text */
  fullWidth?: boolean
  /** Tooltip for the value cell. Falls back to `value` when it's a plain
   * string — set this explicitly when `value` is a non-string node (e.g. a
   * link button) so truncated text still has a hover title. */
  title?: string
}

interface FieldsGridProps {
  fields: FieldDef[]
  className?: string
}

/** Collapse to single column below this pixel width */
const COLLAPSE_BELOW = 600

export function FieldsGrid({ fields, className }: FieldsGridProps) {
  const { ref, width } = useElementWidth<HTMLDivElement>()
  // null = first render (width not yet measured) → assume wide / 2-col
  const singleCol = width !== null && width < COLLAPSE_BELOW

  return (
    <div ref={ref} className={className}>
      <div className={cn("grid", singleCol ? "grid-cols-1" : "grid-cols-2")}>
        {fields.map((field, i) => {
          const isLastAndOdd =
            !singleCol && i === fields.length - 1 && fields.length % 2 === 1
          return (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: static field list
              key={i}
              className={cn(
                "flex items-center justify-between gap-3 border-b border-grey4 px-4 py-2.5 last:border-b-0",
                (field.fullWidth || isLastAndOdd || singleCol) && "col-span-2",
              )}
            >
              {/* Fixed width (not just capped) so every row's value/input
                  column starts and sizes the same regardless of label length —
                  a longer label truncates instead of pushing its own input
                  narrower or further right than the row above it. */}
              <span className="w-40 shrink-0 text-xs wrap-anywhere text-grey5">
                {field.label}
              </span>
              {/* Wraps instead of truncating: a long supplier name or article
                  description must stay readable without a hover. */}
              <span
                className="min-w-0 flex-1 text-right text-xs font-medium wrap-anywhere text-white"
                title={
                  field.title ??
                  (typeof field.value === "string" ? field.value : undefined)
                }
              >
                {field.value}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
